
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Grid } from "lucide-react";
import { cn } from "@/lib/utils";
import InBoxIcon from "@/components/icons/InBoxIcon";
import ArchiveIcon from "@/components/icons/ArchiveIcon";
import { Collection } from "@/contexts/collections";
import { INBOX_ID, ARCHIVE_ID } from "@/utils/contentFormUtils";
import { SidebarMenuItem } from "@/components/ui/sidebar/components/menu";
import CollectionItem from "./CollectionItem";

interface CollectionListProps {
  collections: Collection[];
  isLoading: boolean;
  totalCount?: number;
  isCountLoading?: boolean;
  draggingId: string | null;
  dropTargetId: string | null;
  contentDropTargetId: string | null;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
  onContentDragOver: (e: React.DragEvent, id: string) => void;
  onContentDrop: (e: React.DragEvent, id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string) => void;
}

const CollectionList: React.FC<CollectionListProps> = ({
  collections,
  isLoading,
  totalCount,
  isCountLoading,
  draggingId,
  dropTargetId,
  contentDropTargetId,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onContentDragOver,
  onContentDrop,
  onEdit,
  onDelete,
  onToggleVisibility
}) => {
  const location = useLocation();
  
  const getDroppableClass = (isActive: boolean, isContentDropTarget: boolean = false) => {
    if (isContentDropTarget) {
      return "bg-accent";
    }
    
    return cn("transition-colors duration-200", {
      "bg-accent": isActive,
      "hover:bg-accent/50": !isActive
    });
  };
  
  return (
    <>
      <SidebarMenuItem className="h-8 mb-2">
        <Link 
          to="/dashboard" 
          className={cn("flex items-center justify-between px-3 py-0 w-full h-8 text-sm hover:bg-accent/50 transition-colors", {
            "bg-accent": location.pathname === "/dashboard"
          })}
        >
          <div className="flex items-center">
            <Grid className="h-5 w-5 mr-3" />
            <span>All Content</span>
          </div>
          {!isCountLoading && totalCount !== undefined && (
            <span className="text-muted-foreground">{totalCount}</span>
          )}
        </Link>
      </SidebarMenuItem>
      
      <SidebarMenuItem className="h-8 mb-2 relative">
        <div
          className={cn(
            "flex items-center px-3 py-0 w-full h-8 text-sm transition-colors",
            getDroppableClass(location.pathname === "/inbox"),
            {
              "bg-accent": contentDropTargetId === INBOX_ID
            }
          )}
          onDragOver={(e) => onContentDragOver(e, INBOX_ID)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onContentDrop(e, INBOX_ID)}
          data-collection-id={INBOX_ID}
        >
          <Link 
            to="/inbox" 
            className="flex items-center w-full h-full"
          >
            <div className="h-5 w-5 mr-3 flex items-center justify-center">
              <InBoxIcon size={18} />
            </div>
            <span>In Box</span>
          </Link>
        </div>
      </SidebarMenuItem>
      
      {isLoading ? (
        <SidebarMenuItem className="h-8">
          <div className="px-3 py-0 text-sm text-muted-foreground">Loading collections...</div>
        </SidebarMenuItem>
      ) : collections && collections.length > 0 ? (
        collections.map((collection, index) => (
          <SidebarMenuItem 
            key={`collection-${collection.id}`} 
            className={cn("mb-2", {
              "mb-2": index < collections.length - 1,
            })}
          >
            <CollectionItem
              collection={collection}
              isActive={location.pathname === `/collection/${collection.id}`}
              draggingId={draggingId}
              dropTargetId={dropTargetId}
              contentDropTargetId={contentDropTargetId}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              onContentDragOver={onContentDragOver}
              onContentDrop={onContentDrop}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
            />
          </SidebarMenuItem>
        ))
      ) : (
        <SidebarMenuItem className="h-8">
          <div className="px-3 py-0 text-sm text-muted-foreground">No collections yet</div>
        </SidebarMenuItem>
      )}
      
      <SidebarMenuItem className="h-8 relative">
        <div
          className={cn(
            "flex items-center px-3 py-0 w-full h-8 text-sm transition-colors",
            getDroppableClass(location.pathname === "/archive"),
            {
              "bg-accent": contentDropTargetId === ARCHIVE_ID
            }
          )}
          onDragOver={(e) => onContentDragOver(e, ARCHIVE_ID)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onContentDrop(e, ARCHIVE_ID)}
          data-collection-id={ARCHIVE_ID}
        >
          <Link 
            to="/archive" 
            className="flex items-center w-full h-full"
          >
            <div className="h-5 w-5 mr-3 flex items-center justify-center">
              <ArchiveIcon size={18} />
            </div>
            <span>Archive</span>
          </Link>
        </div>
      </SidebarMenuItem>
    </>
  );
};

export default CollectionList;
